// 261005 E1-P1 / E5 — `WorkbenchSideHead` (tên truyện + việc tiếp theo, đầu thanh trái) và `WorkbenchStrips` (dải job / lỗi / Phần mới: dòng kẻ mảnh).
import React from 'react';
import { Button } from 'antd';
import type { WorkbenchStageKey, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { BgJobApi } from './useBgJob';
import type { ScriptJobApi } from './useScriptJob';
import AppendRunBar from './AppendRunBar';
import NewStoryRunBar from './NewStoryRunBar';
import { Strip } from './WbSlot';
import { useWorkbenchCtx } from './WorkbenchContext';
import ReleaseJobsLink from './ReleaseJobsLink';
import { autoRepairJobLineOf } from './autoRepairView';
import { bgJobLine, hideBgRunningKinds, jobBand, nextActionLabel, nextActionTarget, WorkbenchTarget } from './workbenchView';

interface SideHeadProps {
  storyName?: string;
  workbench: WorkbenchView | null;
}

export const WorkbenchSideHead: React.FC<SideHeadProps> = ({ storyName, workbench }) => (
  <div className="wb-side-head">
    <span className="wb-name" title={workbench?.story.name ?? storyName}>{workbench?.story.name ?? storyName ?? 'Chuyển thể'}</span>
    {workbench && <span className="wb-side-meta">{workbench.outlineFlags.rawChapters} chương nguồn · {workbench.outlineFlags.episodesPlanned} tập</span>}
  </div>
);

/** "Tiếp theo: …" — một dòng chữ nhỏ mờ ở thanh trên (nhãn là link tới việc đó). */
export const NextLine: React.FC<{ workbench: WorkbenchView | null; onGo: (t: WorkbenchTarget) => void; hidden?: boolean }> = ({ workbench, onGo, hidden }) => {
  const next = workbench?.nextAction ?? null;
  if (!workbench || !next || hidden) return null;
  const target = nextActionTarget(next, workbench.episodes.map((e) => ({ ep: e.ep, written: e.written })));
  const label = nextActionLabel(next.label);
  return (
    <span className="wb-nextline" title={label}>
      Tiếp theo: {target ? <button type="button" className="wb-nextlink" onClick={() => onGo(target)}>{label} →</button> : label}
    </span>
  );
};

interface StripsProps {
  workbench: WorkbenchView | null;
  loadError: boolean;
  onRetry: () => void;
  bgJob: BgJobApi;
  scriptJob: ScriptJobApi;
  /** 261005 E4-P4b — dải "Phần mới": id đợt đã đóng thông báo + hàm đóng. */
  appendDismissedId: string | null;
  onDismissAppend: (id: string) => void;
  /** 261023 E3-P3c1 — chặng đang mở (dải truyện mới nhường nút chính cho chặng chứa nút thật). */
  stage: WorkbenchStageKey;
}

const WorkbenchStrips: React.FC<StripsProps> = ({ workbench, loadError, onRetry, bgJob, scriptJob, appendDismissedId, onDismissAppend, stage }) => {
  const { goTo } = useWorkbenchCtx();
  // Job chia tập đã có dòng riêng bên dưới ⇒ bỏ dòng ⏳ trùng của nó trong dải.
  // F36: phút "(n′)" tự chạy lên dù không có lượt poll (workbench poll 5s chỉ khi còn job); job viết đã có Strip riêng khi `scriptJob` đang theo dõi.
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 20000); return () => clearInterval(t); }, []);
  // 3j2b-2: job `auto_repair` ⇒ «Máy đang sửa tập n…» (không «Đang viết kịch bản ở nền … job <mã>»).
  const autoLine = autoRepairJobLineOf(workbench);
  const hideKinds = [...hideBgRunningKinds(bgJob.bgJob?.key), ...(scriptJob.scriptJob || autoLine ? (['write'] as const) : [])];
  const band = jobBand(workbench?.jobs, now, { hideRunningJobId: bgJob.bgJob?.key === 'episodes' ? bgJob.bgJob.id : null, hideRunningKinds: hideKinds });
  const failedCount = workbench?.jobs.failed.length ?? 0;
  // Chỉ mời gỡ tay khi job đã chạy quá 80% hạn tự gỡ của CHÍNH loại việc đó (viết kịch bản 120′ khoẻ vẫn chạy 30′+); thiếu hạn ⇒ coi 30′.
  const staleJobs = (workbench?.jobs.running ?? []).filter((j) => j.startedAt && now - new Date(j.startedAt).getTime() > 0.8 * (j.maxMinutes ?? 30) * 60_000);

  return (
    <div className="wb-strips">
      {loadError && (
        <Strip tone="warn" action={<Button size="small" type="link" onClick={onRetry}>Thử lại</Button>}>Không tải được dữ liệu của truyện</Strip>
      )}

      <AppendRunBar dismissedId={appendDismissedId} onDismiss={onDismissAppend} />
      <NewStoryRunBar stage={stage} dismissedId={appendDismissedId} onDismiss={onDismissAppend} />

      {band && band.map((line, i) => <Strip key={i} role="status" tone={failedCount > 0 ? 'bad' : 'run'}>{line}</Strip>)}
      {/* 261015: job kẹt do pod chết — gỡ tay thay vì chờ tới 120′. Chỉ hiện với job đã chạy > 80% hạn của loại việc (việc khoẻ không bị mời gỡ nhầm). */}
      {staleJobs.length > 0 && (
        <Strip tone="idle" action={<ReleaseJobsLink jobs={staleJobs} onReleased={onRetry} />}>Việc nền chạy lâu bất thường (server vừa khởi động lại?)</Strip>
      )}

      {bgJob.bgJob && (
        <Strip tone="run">
          {bgJobLine(bgJob.bgJob.key, bgJob.progressText, workbench?.jobs.running, bgJob.bgJob.id)}
        </Strip>
      )}
      {autoLine && <Strip tone="run" role="status">{autoLine}</Strip>}
      {scriptJob.scriptJob && !autoLine && (
        <Strip tone="run">
          Đang viết kịch bản ở nền — {scriptJob.progress ?? 'Đang bắt đầu…'} · job {scriptJob.scriptJob.id.slice(0, 8)}… Nút chạy lại được khoá cho tới khi job kết thúc.
        </Strip>
      )}
      {!scriptJob.scriptJob && scriptJob.notice && (
        <Strip
          tone={scriptJob.notice.type === 'success' ? 'ok' : scriptJob.notice.type === 'error' ? 'bad' : scriptJob.notice.type === 'warning' ? 'warn' : 'idle'}
          action={scriptJob.notice.exit === 'openOverview'
            ? <Button size="small" type="link" onClick={() => goTo({ stage: 'skeleton', intent: 'openOverview' })}>Mở Tổng quan truyện</Button>
            : undefined}
        >{scriptJob.notice.message}</Strip>
      )}
    </div>
  );
};

export default WorkbenchStrips;
